import { jwtDecode } from 'jwt-decode';

interface DecodedToken {
  exp?: number;
  sub?: string;
  preferred_username?: string;
  email?: string;
  [key: string]: any;
}

const CLOCK_SKEW_SECONDS = 60; // allow 60s skew to avoid premature expiry

const getRefreshToken = (): string | null => localStorage.getItem('refresh_token');

/** True when access token is present and not expired (does not clear storage). */
export const isAccessTokenValid = (): boolean => {
  const token = localStorage.getItem('access_token');
  if (!token) return false;

  try {
    const decoded = jwtDecode<DecodedToken>(token);
    const currentTime = Date.now() / 1000;
    if (decoded.exp && decoded.exp < (currentTime - CLOCK_SKEW_SECONDS)) {
      return false;
    }
    return true;
  } catch {
    return false;
  }
};

/**
 * Session is considered active if the access token is valid OR a refresh token
 * is still available so the session can be renewed. Never wipe the refresh token
 * just because the short-lived access token expired.
 */
export const isAuthenticated = (): boolean => {
  if (isAccessTokenValid()) {
    return true;
  }
  return !!getRefreshToken();
};

export const clearAuthData = (): void => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
  localStorage.removeItem('user_info');
  sessionStorage.clear();
};

export const getToken = (): string | null => {
  return localStorage.getItem('access_token');
};

export const getUserInfo = (): DecodedToken | null => {
  const token = getToken();
  if (!token) return null;

  try {
    return jwtDecode<DecodedToken>(token);
  } catch {
    return null;
  }
};

/** Access token expired or missing. Does not clear storage. */
export const isTokenExpired = (): boolean => {
  return !isAccessTokenValid();
};

/**
 * Remove clearly unusable tokens only when there is no way to renew the session.
 * An expired access token alone must not clear the refresh token.
 */
export const validateAndCleanTokens = (): void => {
  const token = localStorage.getItem('access_token');
  const refresh = getRefreshToken();

  if (!token && !refresh) {
    return;
  }

  if (!token) {
    return;
  }

  try {
    jwtDecode<DecodedToken>(token);
    // Keep expired access tokens if refresh can renew them; axios/ProtectedRoute will refresh.
  } catch (error) {
    console.error('[AUTH DEBUG] Invalid access token during validation:', error);
    localStorage.removeItem('access_token');
    if (!refresh) {
      clearAuthData();
    }
  }
};

export const forceClearAuth = (): void => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
  localStorage.removeItem('user_info');
  sessionStorage.clear();

  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (key.includes('token') || key.includes('auth') || key.includes('user'))) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach(key => {
    localStorage.removeItem(key);
  });
};
